import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  GraphQlDmlVersion,
  Maybe,
  useGetDataModelVersionsByIdQuery,
  useListDataModelVersionsQuery,
} from 'types/graphql-apollo-v2';
import { parseRefId } from 'utils/misc';
import { generateSchemas } from 'utils/schema-parser';

export interface Property {
  id: string;
  tenant: string;
  orderIndex: number;
  schema?: Schema;
  name: string;
  directives: string[];
  type: string;
}

export interface Schema {
  id: string;
  tenant: string;
  orderIndex: number;
  project: Project;
  instanceCount: number;
  name: string;
  properties: Property[];
}

export interface Project {
  id: string;
  space: string;
  name: string;
  description: string;
  createdTime: string;
  graphQLSchemaString: string;
  schemas: Schema[];
  tenant: string;
  versions: Array<ApiVersion>;
}

export interface ProjectListItem {
  id: string;
  space: string;
  name: string;
  tenant: string;
  versions: Array<ApiVersion>;
}

export type ApiVersion = {
  version?: string;
  dataModel: string;
};

function transformDmlToProject(
  tenant: string,
  dmls?: Maybe<GraphQlDmlVersion>[]
) {
  const transformedData: Project[] = [];
  dmls?.forEach((api) => {
    const existingProject = transformedData.find(
      (project) => project.id === api?.externalId && project.space === api.space
    );
    if (existingProject) {
      existingProject.versions.push({
        version: api?.version,
        dataModel: api?.graphQlDml || '',
      });
    } else {
      transformedData.push({
        id: api?.externalId || '',
        space: api?.space || '',
        name: api?.name || '',
        description: api?.description || '',
        createdTime: api?.createdTime || '',
        graphQLSchemaString: api?.graphQlDml || '',
        tenant,
        schemas: generateSchemas(api?.graphQlDml || '', tenant),
        versions: [
          {
            version: api?.version,
            dataModel: api?.graphQlDml || '',
          },
        ],
      });
    }
  });
  return transformedData;
}

// Todo: change the function input variables to just id/refid
export const useGetProjectQuery = (refId?: string | null) => {
  const { dataModel, dataModelVersion, space } = parseRefId(refId || '');
  const { data, ...queryProps } = useGetDataModelVersionsByIdQuery({
    variables: { space, dataModel },
  });
  const { tenantId } = useParams<{ tenantId: string }>();
  const newData = useMemo(() => {
    const transformedData: Project[] = transformDmlToProject(
      tenantId,
      data?.graphQlDmlVersionsById?.items
    ).map((project) => {
      const graphQlDml =
        project.versions.find((v) => v.version === dataModelVersion)
          ?.dataModel || '';

      return {
        ...project,
        graphQLSchemaString: graphQlDml,
        schemas: generateSchemas(graphQlDml, tenantId),
      };
    });
    return { Project: transformedData };
  }, [data]);

  return { data: newData, ...queryProps };
};

export const useGetAddSolutionsForDrawerQuery = ({
  notifyOnNetworkStatusChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  variables: { limit, cursor, tenant, existingSolutionIds },
}: {
  notifyOnNetworkStatusChange: boolean;
  variables: {
    limit: number;
    cursor: string | null;
    tenant: string;
    existingSolutionIds: string[];
  };
}) => {
  const { data, ...queryProps } = useListDataModelVersionsQuery({
    notifyOnNetworkStatusChange,
    variables: {
      limit,
      cursor,
    },
  });
  const { tenantId } = useParams<{ tenantId: string }>();
  const newData = useMemo(() => {
    const transformedData: ProjectListItem[] = transformDmlToProject(
      tenantId,
      data?.listGraphQlDmlVersions?.items
    );
    return { Project: transformedData };
  }, [data]);
  return { data: newData, ...queryProps };
};
